"use client"

import { Text } from "@/elements"
import { TextArea } from "@/elements/TextArea"
import { useMemo, useState } from "react"
import { DatePicker } from "./DatePicker"
import { useFormContext, Controller } from "react-hook-form"
import { PostAJobModalFormValues } from "./types"
import { PositionInput } from "./PositionInput"
import { LocationInput } from "./LocationInput"
import { WorkTypeInput } from "./WorkTypeInput"
import clsx from "clsx"
import { ampli } from "@/ampli"
import adjustDateForTimezone from "@/utils/dates"
import { decodeGraphqlNodeId } from "@/tracking"

interface Props {
  stepOneDisplayFields: { position: string; location: string; workType: string }
  onChangeStepOneDisplayField: (fieldName: string, value: string) => void
  jobPostId: string
}

export const StepOne: React.FC<Props> = ({ stepOneDisplayFields, onChangeStepOneDisplayField, jobPostId }) => {
  const { control, watch } = useFormContext<PostAJobModalFormValues>()
  const [showDatePicker, setShowDatePicker] = useState(false)
  const startDate = watch("startDate")
  const endDate = watch("endDate")
  const datesFlexible = watch("datesFlexible")
  const remote = watch("remote")

  const formattedDates = useMemo(() => {
    const start = startDate
      ? adjustDateForTimezone(startDate?.toISOString()!)?.toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
        })
      : null
    const end = endDate
      ? adjustDateForTimezone(endDate.toISOString())?.toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
        })
      : null

    if (!start && !end) return "Dates"

    if (start === end) return start

    if (!end) return start

    if (!start) return end

    return `${start} - ${end}`
  }, [startDate, endDate])

  return (
    <div className="max-w-screen relative flex flex-col">
      {showDatePicker && (
        <div className="absolute left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 transform">
          <DatePicker setShowDatePicker={setShowDatePicker} jobPostId={jobPostId} />
        </div>
      )}
      <div className="pb-4 pt-10">
        <div className="mb-8">
          <Text className="text-fg-2" variant="body2">
            Step 1 of 3:{" "}
            <Text element="span" variant="body2" className="text-fg-1">
              Create a job post
            </Text>
          </Text>
        </div>
        <Text variant="body1">I&apos;m looking to hire:</Text>
        <div className="mt-2 flex flex-col gap-3">
          <div className="flex flex-row">
            <Text element="span" variant="h4">
              a
            </Text>
            <div className="ml-4 flex flex-1 flex-row">
              <PositionInput
                displayValue={stepOneDisplayFields.position}
                onChangeDisplayValue={value => onChangeStepOneDisplayField("position", value)}
              />
            </div>
          </div>
          <div className="flex flex-row">
            <Text element="span" variant="h4">
              {remote ? "working" : "in"}
            </Text>
            <div className="ml-4 flex flex-1 flex-row">
              <LocationInput
                displayValue={stepOneDisplayFields.location}
                onChangeDisplayValue={value => onChangeStepOneDisplayField("location", value)}
              />
            </div>
          </div>
          <div className="flex flex-col">
            <div className="relative flex flex-row">
              <Text element="span" variant="h4">
                on
              </Text>

              <div className="relative ml-4 cursor-pointer">
                <div onClick={() => setShowDatePicker(!showDatePicker)} tabIndex={0}>
                  <Text
                    element="span"
                    variant="h4"
                    className={clsx(formattedDates !== "Dates" ? "text-primary-3" : "text-primary-2")}>
                    {formattedDates}
                  </Text>
                </div>
              </div>
            </div>
            {datesFlexible && (
              <Text variant="label" className="mt-1">
                * Dates flexible
              </Text>
            )}
          </div>
          <div className="relative flex flex-row">
            <Text element="span" variant="h4">
              for
            </Text>

            <div className="relative ml-4 flex flex-1 cursor-pointer flex-row">
              <WorkTypeInput
                displayValue={stepOneDisplayFields.workType}
                onChangeDisplayValue={value => onChangeStepOneDisplayField("workType", value)}
              />
            </div>
          </div>
        </div>
        <div className="mt-10">
          <Text variant="label">Describe this job:</Text>
        </div>
        <div className="mt-4">
          <Controller
            name="description"
            control={control}
            render={({ field: { onBlur, ...rest } }) => (
              <div>
                <TextArea
                  onBlur={() => {
                    ampli.postAJobSetDetail({
                      job_id: decodeGraphqlNodeId(jobPostId).numericalId || "",
                      job_post_field: "description",
                      job_post_field_value: rest.value,
                    })
                    onBlur()
                  }}
                  className="min-h-[112px] w-full"
                  placeholder="Add a description"
                  maxLength={5000}
                  {...rest}
                />
              </div>
            )}
          />
        </div>
      </div>
    </div>
  )
}
